import { useNavigate } from 'react-router-dom';
import { cn } from '@/components/ui/utils';
import { InternBadge } from './shared/Badge';
import type { SkillItem } from '../types';

interface Props {
  item: SkillItem;
  onComplete?: (id: string) => void;
}

const statusVariant: Record<string, 'pending' | 'active' | 'completed'> = {
  suggested: 'pending',
  exploring: 'active',
  explored:  'completed',
};

const statusLabel: Record<string, string> = {
  suggested: 'Suggested',
  exploring: 'Exploring',
  explored:  'Explored',
};

export function SkillCard({ item, onComplete }: Props) {
  const navigate = useNavigate();

  return (
    <div
      className={cn(
        'rounded-[4px] border bg-white cursor-pointer group',
        'transition-all duration-200',
        'shadow-[0_2px_8px_rgba(27,58,45,0.08)] hover:shadow-[0_4px_16px_rgba(27,58,45,0.12)]',
        'overflow-hidden'
      )}
      style={{ borderColor: 'rgba(27,58,45,0.15)' }}
      onClick={() => navigate(`/intern/skill/${item.id}`)}
      role="article"
      aria-label={`Skill: ${item.title}`}
    >
      {/* Gold top accent bar */}
      <div className="h-[3px] w-full" style={{ background: 'var(--color-gold)' }} aria-hidden />

      <div className="p-5">
        <div className="flex items-start justify-between gap-3 mb-2">
          <h3
            className="text-[18px] font-[600] leading-snug flex-1"
            style={{ color: 'var(--color-near-black)' }}
          >
            {item.title}
          </h3>
          <InternBadge variant={statusVariant[item.status] ?? 'pending'}>
            {statusLabel[item.status]}
          </InternBadge>
        </div>

        {item.description && (
          <p className="text-[15px] text-[var(--color-charcoal)] leading-relaxed line-clamp-2 mb-3">
            {item.description}
          </p>
        )}

        {/* Links */}
        {item.links && item.links.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {item.links.map((link) => (
              <a
                key={link.url}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(
                  'inline-flex items-center gap-1 px-2 py-0.5 rounded-[4px]',
                  'text-[12px] font-[500] border transition-colors duration-200',
                  'bg-[var(--color-warm-white)] border-[var(--border)]',
                  'text-[var(--color-forest)] hover:bg-[var(--color-forest)] hover:text-[var(--color-warm-white)]'
                )}
                onClick={(e) => e.stopPropagation()}
              >
                {link.label}
                <span aria-hidden className="opacity-60">↗</span>
              </a>
            ))}
          </div>
        )}

        {onComplete && item.status !== 'explored' && (
          <div className="mt-3 flex justify-end" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => onComplete(item.id)}
              className="text-[13px] font-[500] text-[var(--color-forest)] hover:underline"
            >
              Mark explored ✓
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
